import { AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable } from 'rxjs';

import { gpSearchFields, hvSearchFields, schoolNurseSearchFields } from '@Constants';

import { FiltersService, AuthService } from '@Services';

import { User } from '@Models';

import { dateValidator, formatKey } from '@Utils';

import { MultiSelectionComponent } from '../multi-selection/multi-selection.component';
import { NoDataSelectComponent } from '../no-data-select/no-data-select.component';

@Component({
  selector: 'app-dashboard-form',
  standalone: true,
  imports: [ReactiveFormsModule, AsyncPipe, MultiSelectionComponent, NoDataSelectComponent],
  templateUrl: './dashboard-form.component.html',
  styleUrl: './dashboard-form.component.css',
})
export class DashboardFormComponent implements OnInit {
  searchForm!: FormGroup;
  formSubmitted = false;
  practices$: Observable<{ value: string; label: string }[]> | null = null;
  schools$: Observable<{ value: string; label: string }[]> | null = null;
  vaccines$: Observable<{ value: string; label: string }[]> | null = null;
  ethnicities$: Observable<{ value: string; label: string }[]> | null = null;
  wards$: Observable<{ value: string; label: string }[]> | null = null;
  imds$: Observable<{ value: string; label: string }[]> | null = null;
  schoolYears$: Observable<{ value: string; label: string }[]> | null = null;
  localEducationAuthorities$: Observable<{ value: string; label: string }[]> | null = null;
  refusalReasons$: Observable<{ value: string; label: string }[]> | null = null;

  dropdownSchoolArray: { key: string; value: string }[] = [
    { key: '0', value: '0' },
    { key: '1', value: '1' },
    { key: '2', value: '2' },
    { key: '3', value: '3' },
    { key: '4', value: '4' },
    { key: '5', value: '5' },
    { key: '6', value: '6' },
    { key: '7', value: '7' },
    { key: '8', value: '8' },
    { key: '9', value: '9' },
    { key: '10', value: '10' },
    { key: '11', value: '11' },
    { key: '12', value: '12' },
    { key: '13', value: '13' },
  ];

  dropdownAgeInYears: { key: string; value: string }[] = [
    { key: '0', value: '0' },
    { key: '1', value: '1' },
    { key: '2', value: '2' },
    { key: '3', value: '3' },
    { key: '4', value: '4' },
    { key: '5', value: '5' },
    { key: '6', value: '6' },
  ];

  user: User | null = null;

  isDetailsOpen = true;
  appliedFilters: { key: string; value: string }[] = [];

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _fb: FormBuilder,
    private _filtersService: FiltersService,
    private _authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();

    this._route.queryParams.subscribe((params) => {
      Object.keys(params).forEach((key) => {
        if (this.searchForm.contains(key)) {
          this.searchForm.get(key)?.setValue(params[key]);
        }
      });
    });
    this.getAllPractices();
    this.getAllSchools();
    this.getAllVaccines();
    this.getAllEthnicities();
    this.getAllWards();
    this.getAllImds();
    this.getAllSchoolYears();
    this.getAllLocalAuthorities();
    this.getAllRefusalReasons();

    //get the user role
    const access_token = sessionStorage.getItem('access_token');
    const refresh_token = sessionStorage.getItem('refresh_token');
    if (access_token && refresh_token) {
      this._authService.user$.subscribe((user) => {
        if (!this.user && !user) {
          this._authService.setSession(access_token, refresh_token);
        }
      });
      this._authService.user$.subscribe((user) => {
        this.user = user;
      });
    }
  }

  initializeForm() {
    this.searchForm = this._fb.group(
      {
        startDateDoB: ['', dateValidator()],
        endDateDoB: ['', dateValidator()],
        postcode: [''],
        immunisationStatus: ['-1'],
        SchoolCode: [[]],
        SchoolYear: ['-2'],
        AgeGroup: ['-1'],
        AgeInYears: ['-1'],
        sex: ['-1'],
        ethnicity: ['-1'],
        wardCode: ['-1'],
        imd: ['-1'],
        englishFirstLanguage: ['-1'],
        UptoDate: [[]],
        UpToDatePrimary: ['-1'],
        UpToDate12Month: ['-1'],
        UpToDatePreschool: ['-1'],
        HasRefusals: ['-1'],
        HasSuspensions: ['-1'],
        PracticeCode: ['-1'],
        MissingVaccine: [[]],
        sciD_Result: ['-1'],
        CurrentCover: ['-1'],
        NextCover: ['-1'],
        CohortIsNIPE72Hour: ['-1'],
        CohortIsGP68WeeksReview: ['-1'],
        CohortIsNewbornBloodSpotScreening: ['-1'],
        CohortIsNewbornHearingScreening: ['-1'],
        LocalEducationAuthorityCode: [[]],
        RefusalReasons: [[]],
        pageNumber: 1,
      },
      { updateOn: 'submit' },
    );
  }

  exceptionKeys = ['SchoolYear'];

  onSubmit() {
    this.formSubmitted = true;
    const formValues = this.searchForm.value;
    console.log(formValues);

    const queryParams = Object.keys(formValues)
      .filter((key) => {
        if (key === 'MissingVaccine' && Array.isArray(formValues[key])) {
          // Include only if the array is not empty and doesn't just contain the default value
          return formValues[key].length > 0 && !formValues[key].every((val) => val === '-1' || val === '-2');
        }
        if (key === 'SchoolCode' && Array.isArray(formValues[key])) {
          // Include only if the array is not empty and doesn't just contain the default value
          return formValues[key].length > 0 && !formValues[key].every((val) => val === '-1' || val === '-2');
        }
        if (key === 'UptoDate' && Array.isArray(formValues[key])) {
          // Include only if the array is not empty and doesn't just contain the default value
          return formValues[key].length > 0 && !formValues[key].every((val) => val === '-1' || val === '-2');
        }
        if (this.exceptionKeys.includes(key)) {
          return formValues[key] !== null && formValues[key] !== '' && formValues[key] !== '-2';
        }
        return (
          formValues[key] !== null && formValues[key] !== '' && formValues[key] !== '-1' && formValues[key] !== '-2'
        );
      })
      .reduce((obj: Record<string, unknown>, key: string) => {
        // Expand MissingVaccine array into multiple params
        if (key === 'MissingVaccine' && Array.isArray(formValues[key])) {
          obj[key] = formValues[key].filter((val: string) => val !== '-1' && val !== '-2');
        } else {
          obj[key] = formValues[key];
        }
        return obj;
      }, {});

    queryParams['pageNumber'] = 1;

    // --- CUSTOM SERIALIZATION ---
    // Convert array params to repeated key-value pairs
    const expandedParams: Record<string, unknown> = {};
    Object.keys(queryParams).forEach((key) => {
      const value = queryParams[key];
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (expandedParams[key]) {
            if (Array.isArray(expandedParams[key])) {
              expandedParams[key].push(v);
            } else {
              expandedParams[key] = [expandedParams[key], v];
            }
          } else {
            expandedParams[key] = [v];
          }
        });
      } else {
        expandedParams[key] = value;
      }
    });

    this._router
      .navigate([`/vaccine-history`], {
        queryParams: expandedParams,
      })
      .then(() => {
        (document.activeElement as HTMLElement).blur();
      });
  }

  toggleDetails(event: Event): void {
    this.isDetailsOpen = (event.target as HTMLDetailsElement).open;
    if (!this.isDetailsOpen) {
      this.getAppliedFilters();
    }
  }

  getAppliedFilters(): void {
    this.appliedFilters = Object.keys(this.searchForm.controls)
      .filter((key) => {
        const control = this.searchForm.get(key);
        return control && control.value && control.value !== '-1' && control.value !== '-2' && key !== 'pageNumber';
      })
      .map((key) => ({
        key: formatKey(key),
        value: this.searchForm.get(key)?.value,
      }));
  }

  isNotEmpty(controlName: string): boolean {
    const control = this.searchForm.get(controlName);
    if (!control || !control.value) {
      return false;
    }

    // Special handling for MissingVaccine when it's an array
    if (controlName === 'MissingVaccine' && Array.isArray(control.value)) {
      // Check if the array has any non-default values
      return control.value.length > 0 && control.value.some((value) => value !== '-1' && value !== '-2');
    }

    // Original logic for other controls
    if (this.exceptionKeys.includes(controlName)) {
      return control.value.trim() !== '' && control.value !== '-2';
    }
    return control.value.trim() !== '' && control.value !== '-1' && control.value !== '-2';
  }

  clearFilters() {
    this.initializeForm();
    (document.activeElement as HTMLElement).blur();

    this.onSubmit();
  }

  getAllPractices() {
    return (this.practices$ = this._filtersService.getAllPractices());
  }

  getAllSchools() {
    return (this.schools$ = this._filtersService.getAllSchools());
  }

  getAllVaccines() {
    return (this.vaccines$ = this._filtersService.getAllVaccines());
  }

  getAllEthnicities() {
    return (this.ethnicities$ = this._filtersService.getAllEthnicities());
  }

  getAllWards() {
    return (this.wards$ = this._filtersService.getAllWards());
  }

  getAllImds() {
    return (this.imds$ = this._filtersService.getAllImds());
  }

  getAllSchoolYears() {
    return (this.schoolYears$ = this._filtersService.getAllSchoolYears());
  }

  getAllLocalAuthorities() {
    return (this.localEducationAuthorities$ = this._filtersService.getAllLocalAuthorities());
  }

  getAllRefusalReasons() {
    return (this.refusalReasons$ = this._filtersService.getAllRefusalReasons());
  }

  shouldDisplayField(type?: string): boolean {
    switch (this.user?.role) {
      case 'gp':
      case 'pd':
        return gpSearchFields.includes(type ?? '');
      case 'hv':
        return hvSearchFields.includes(type ?? '');
      case 'sn':
        return schoolNurseSearchFields.includes(type ?? '');
      default:
        return false;
    }
  }

  getMissingVaccinesFromUrl(): string[] {
    const missingVaccine = this.searchForm.value['MissingVaccine'];

    // If missingVaccine is null or undefined, return an empty array
    if (missingVaccine == null) {
      return [];
    }

    // If missingVaccine is already an array, return it
    if (Array.isArray(missingVaccine)) {
      return missingVaccine;
    }

    // If missingVaccine is a string, convert it to an array with one element
    return [missingVaccine];
  }

  onMissingVaccineItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('MissingVaccine')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getUptoDateFromUrl(): string[] {
    return this.searchForm.value['UptoDate'];
  }

  onUptoDateItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('UptoDate')?.setValue(selectedValues);
    }
  }

  getSchoolFromUrl(): string[] {
    return this.searchForm.value['SchoolCode'];
  }

  onSchoolItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('SchoolCode')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getLAFromUrl(): string[] {
    const la = this.searchForm.value['LocalEducationAuthorityCode'];

    // If missingVaccine is null or undefined, return an empty array
    if (la == null) {
      return [];
    }

    // If missingVaccine is already an array, return it
    if (Array.isArray(la)) {
      return la;
    }

    // If missingVaccine is a string, convert it to an array with one element
    return [la];
  }

  onLAItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('LocalEducationAuthorityCode')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }

  getRefusalReasonsFromUrl(): string[] {
    return this.searchForm.value['RefusalReasons'];
  }

  onRefusalReasonsItemSelectedChange(selectedItems: { label: string; value: string }[]): void {
    if (this.searchForm) {
      const selectedValues = selectedItems.map((item) => item.value);
      this.searchForm.get('RefusalReasons')?.setValue(selectedValues);

      this.searchForm.markAsDirty();
    }
  }
}
